// src/pages/admin/SupportManagement.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, IconButton, Typography, useMediaQuery, useTheme,
  Drawer, CircularProgress, Alert, Snackbar, Button, TextField,
  InputAdornment, Chip, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl,
  SelectChangeEvent, Divider, Avatar,
} from '@mui/material';
import {
  Search, Refresh, Close, SupportAgent, Receipt, Send,
  CheckCircle, Schedule, Lock, Inbox, Person, AdminPanelSettings,
  Phone, Email,
} from '@mui/icons-material';

import Slidemenu   from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListAllTicketsQuery,
  useGetConversationQuery,
  useSendReplyMutation,
  useUpdateTicketStatusMutation,
  SupportTicket,
  TicketStatusNum,
  STATUS_LABEL,
} from '../../features/admin/supportApi.ts';

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  TicketStatusNum,
  { bgcolor: string; color: string; icon: React.ReactNode }
> = {
  0: { bgcolor: '#fff3cd', color: '#856404',  icon: <Inbox      sx={{ fontSize: 13 }} /> },
  1: { bgcolor: '#cfe2ff', color: '#0a58ca',  icon: <Schedule   sx={{ fontSize: 13 }} /> },
  2: { bgcolor: '#d1f4e0', color: '#00783e',  icon: <CheckCircle sx={{ fontSize: 13 }} /> },
  3: { bgcolor: '#e4e5e7', color: '#616161',  icon: <Lock       sx={{ fontSize: 13 }} /> },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (d?: string) =>
  d ? new Date(d).toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '-';

const formatDateShort = (d?: string) =>
  d ? new Date(d).toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
  }) : '-';

// ── Component ─────────────────────────────────────────────────────────────────

const SupportManagement: React.FC = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [tabValue,       setTabValue]       = useState(() => {
    const saved = Number(localStorage.getItem('support_management_tab'));
    return Number.isInteger(saved) && saved >= 0 && saved <= 4 ? saved : 0;
  });
  const [searchTerm,     setSearchTerm]     = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [openChat,       setOpenChat]       = useState(false);
  const [replyText,      setReplyText]      = useState('');
  const [replyStatus,    setReplyStatus]    = useState<TicketStatusNum>(1);
  const [snackbar,       setSnackbar]       = useState({
    open: false, message: '', severity: 'success' as 'success' | 'error',
  });
  const bottomRef = useRef<HTMLDivElement>(null);

  // ── API hooks ──────────────────────────────────────────────────────────────

  const { data, isLoading, isError, refetch } = useListAllTicketsQuery(undefined, {
    pollingInterval: 15000,   // keep the ticket list (and unread status) fresh in the background
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const {
    data: convData, isFetching: loadingChat, refetch: refetchConv,
  } = useGetConversationQuery(selectedTicket?.id ?? 0, {
    skip: !selectedTicket,
    pollingInterval: 5000,   // while a chat is open, pick up new messages from the other side automatically
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [sendReply,    { isLoading: sendingReply }]   = useSendReplyMutation();
  const [updateStatus, { isLoading: updatingStatus }] = useUpdateTicketStatusMutation();

  const tickets: SupportTicket[] = data?.tickets ?? [];
  const replies = convData?.replies ?? [];

  // Scroll to bottom when replies change
  useEffect(() => {
    if (openChat) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [replies, openChat]);

  // ── Tabs: 0=All, 1=Open(0), 2=InProgress(1), 3=Resolved(2), 4=Closed(3) ──
  const TAB_STATUS: (TicketStatusNum | -1)[] = [-1, 0, 1, 2, 3];
  const TAB_LABELS = ['All', 'Open', 'In Progress', 'Resolved', 'Closed'];

  const filteredTickets = tickets.filter((t) => {
    const term = searchTerm.toLowerCase();
    const matchSearch =
      t.subject.toLowerCase().includes(term)          ||
      (t.invoice_no    ?? '').toLowerCase().includes(term) ||
      (t.display_name  ?? '').toLowerCase().includes(term) ||
      (t.email         ?? '').toLowerCase().includes(term) ||
      (t.phone         ?? '').toLowerCase().includes(term);
    const matchTab = tabValue === 0 || t.status === TAB_STATUS[tabValue];
    return matchSearch && matchTab;
  });

  const countByTab = (i: number) =>
    i === 0 ? tickets.length : tickets.filter(t => t.status === TAB_STATUS[i]).length;

  // ── Open conversation ──────────────────────────────────────────────────────

  const handleOpenTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setReplyStatus(ticket.status === 0 ? 1 : ticket.status);
    setReplyText('');
    setOpenChat(true);
  };

  const handleCloseChat = () => {
    setOpenChat(false);
    setSelectedTicket(null);
    setReplyText('');
  };

  // ── Send reply ─────────────────────────────────────────────────────────────

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      await sendReply({
        ticket_id: selectedTicket.id,
        message:   replyText.trim(),
        status:    replyStatus,
      }).unwrap();
      setReplyText('');
      refetchConv();
      refetch();
      showSnackbar('Reply sent!', 'success');
    } catch {
      showSnackbar('Failed to send reply', 'error');
    }
  };

  // ── Quick status update ────────────────────────────────────────────────────

  const handleQuickStatus = async (ticketId: number, status: TicketStatusNum) => {
    try {
      await updateStatus({ ticket_id: ticketId, status }).unwrap();
      showSnackbar(`Status updated to "${STATUS_LABEL[status]}"`, 'success');
      refetch();
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
  };

  // ── Snackbar ──────────────────────────────────────────────────────────────

  const showSnackbar = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  // ── Tab styles ────────────────────────────────────────────────────────────

  const tabSx = {
    '& .MuiTab-root':       { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' },
    '& .Mui-selected':      { color: '#202223', fontWeight: 600 },
    '& .MuiTabs-indicator': { backgroundColor: '#008060', height: 3 },
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={() => setMobileOpen(!mobileOpen)} />
        </Box>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={() => setMobileOpen(false)} mobileOpen={false} />
        </Drawer>

        {/* Main */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={() => setMobileOpen(!mobileOpen)} />

          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

              {/* Page header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Support Management
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => refetch()}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}
                >
                  Refresh
                </Button>
              </Box>

              {/* Stats cards */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 2, mb: 3 }}>
                {([0, 1, 2, 3] as TicketStatusNum[]).map((s) => {
                  const cfg   = STATUS_CONFIG[s];
                  const count = tickets.filter(t => t.status === s).length;
                  return (
                    <Card key={s} sx={{ p: 2, borderRadius: '12px', border: '1px solid #e1e3e5', boxShadow: 'none', textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: cfg.color }}>{count}</Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 500 }}>{STATUS_LABEL[s]}</Typography>
                    </Card>
                  );
                })}
              </Box>

              {/* Main card */}
              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>

                {/* Tabs */}
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs
                    value={tabValue}
                    onChange={(_, v) => { setTabValue(v); localStorage.setItem('support_management_tab', String(v)); setSearchTerm(''); }}
                    sx={{ px: 2, ...tabSx }}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {TAB_LABELS.map((label, i) => (
                      <Tab key={label} label={`${label} (${countByTab(i)})`} />
                    ))}
                  </Tabs>
                </Box>

                {/* Search */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5' }}>
                  <TextField
                    size="small"
                    placeholder="Search by subject, name, email, phone or invoice..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    sx={{ minWidth: 340, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Loading */}
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {/* Error */}
                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">
                      Failed to load tickets.
                      <Button color="inherit" size="small" onClick={() => refetch()} sx={{ ml: 1 }}>Retry</Button>
                    </Alert>
                  </Box>
                )}

                {/* Empty */}
                {!isLoading && !isError && filteredTickets.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <SupportAgent sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>No tickets found</Typography>
                  </Box>
                )}

                {/* ── Desktop table ──────────────────────────────────────── */}
                {!isLoading && !isError && filteredTickets.length > 0 && !isMobile && (
                  <TableContainer sx={{ bgcolor: 'white' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafbfb' }}>
                          {['#', 'Raised By', 'Subject', 'Invoice', 'Status', 'Created', 'Actions'].map(h => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 600, fontSize: '11px',
                                textTransform: 'uppercase', color: '#6d7175',
                                ...(h === 'Actions' ? { textAlign: 'right' } : {}),
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTickets.map(ticket => {
                          const cfg = STATUS_CONFIG[ticket.status];
                          return (
                            <TableRow
                              key={ticket.id}
                              sx={{ '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                              onClick={() => handleOpenTicket(ticket)}
                            >
                              {/* # */}
                              <TableCell>
                                <Typography variant="body2" sx={{ color: '#6d7175', fontSize: '12px' }}>
                                  #{ticket.id}
                                </Typography>
                              </TableCell>

                              {/* Raised By */}
                              <TableCell sx={{ minWidth: 180 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                  {ticket.display_name}
                                </Typography>
                                {ticket.email !== '-' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.3 }}>
                                    <Email sx={{ fontSize: 11, color: '#6d7175' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                                      {ticket.email}
                                    </Typography>
                                  </Box>
                                )}
                                {ticket.phone !== '-' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                    <Phone sx={{ fontSize: 11, color: '#6d7175' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                                      {ticket.phone}
                                    </Typography>
                                  </Box>
                                )}
                                {ticket.is_guest && (
                                  <Chip
                                    label="Guest"
                                    size="small"
                                    sx={{ fontSize: '9px', height: 16, mt: 0.5, bgcolor: '#fff3cd', color: '#856404', fontWeight: 600 }}
                                  />
                                )}
                              </TableCell>

                              {/* Subject */}
                              <TableCell sx={{ maxWidth: 260 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: '#202223', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {ticket.subject}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                                  {(ticket.description ?? '').slice(0, 55)}{(ticket.description?.length ?? 0) > 55 ? '…' : ''}
                                </Typography>
                              </TableCell>

                              {/* Invoice */}
                              <TableCell>
                                {ticket.invoice_no ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Receipt sx={{ fontSize: 14, color: '#6d7175' }} />
                                    <Typography variant="body2">{ticket.invoice_no}</Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>

                              {/* Status */}
                              <TableCell onClick={e => e.stopPropagation()}>
                                <Chip
                                  label={STATUS_LABEL[ticket.status]}
                                  size="small"
                                  icon={cfg.icon as React.ReactElement}
                                  sx={{ fontSize: '12px', fontWeight: 600, bgcolor: cfg.bgcolor, color: cfg.color }}
                                />
                              </TableCell>

                              {/* Created */}
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                  {formatDateShort(ticket.created_at)}
                                </Typography>
                              </TableCell>

                              {/* Actions */}
                              <TableCell align="right" onClick={e => e.stopPropagation()}>
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleOpenTicket(ticket)}
                                    sx={{ textTransform: 'none', fontSize: '12px', borderColor: '#008060', color: '#008060', minWidth: 0, px: 1.5, '&:hover': { bgcolor: '#f0faf7', borderColor: '#006e52' } }}
                                  >
                                    Reply
                                  </Button>
                                  {ticket.status !== 2 && (
                                    <Tooltip title="Mark Resolved">
                                      <IconButton size="small" disabled={updatingStatus} onClick={() => handleQuickStatus(ticket.id, 2)} sx={{ color: '#00783e', '&:hover': { bgcolor: '#d1f4e0' } }}>
                                        <CheckCircle fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {ticket.status !== 3 && (
                                    <Tooltip title="Close Ticket">
                                      <IconButton size="small" disabled={updatingStatus} onClick={() => handleQuickStatus(ticket.id, 3)} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                        <Lock fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* ── Mobile cards ───────────────────────────────────────── */}
                {!isLoading && !isError && filteredTickets.length > 0 && isMobile && (
                  <Box sx={{ p: 2, bgcolor: 'white' }}>
                    {filteredTickets.map(ticket => {
                      const cfg = STATUS_CONFIG[ticket.status];
                      return (
                        <Card
                          key={ticket.id}
                          sx={{ mb: 2, p: 2, borderRadius: '12px', border: '1px solid #e1e3e5' }}
                          onClick={() => handleOpenTicket(ticket)}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                {ticket.subject}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6d7175' }}>
                                {ticket.display_name}
                                {ticket.is_guest && (
                                  <Chip label="Guest" size="small" sx={{ fontSize: '9px', height: 14, ml: 0.5, bgcolor: '#fff3cd', color: '#856404' }} />
                                )}
                              </Typography>
                            </Box>
                            <Chip
                              label={STATUS_LABEL[ticket.status]}
                              size="small"
                              sx={{ fontSize: '10px', fontWeight: 600, bgcolor: cfg.bgcolor, color: cfg.color, alignSelf: 'flex-start' }}
                            />
                          </Box>

                          {/* Contact info */}
                          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                            {ticket.phone !== '-' && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <Phone sx={{ fontSize: 11, color: '#6d7175' }} />
                                <Typography variant="caption" color="text.secondary">{ticket.phone}</Typography>
                              </Box>
                            )}
                            {ticket.email !== '-' && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <Email sx={{ fontSize: 11, color: '#6d7175' }} />
                                <Typography variant="caption" color="text.secondary">{ticket.email}</Typography>
                              </Box>
                            )}
                          </Box>

                          <Typography variant="caption" color="text.secondary">
                            {(ticket.description ?? '').slice(0, 80)}{(ticket.description?.length ?? 0) > 80 ? '…' : ''}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              #{ticket.id} · {formatDateShort(ticket.created_at)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#008060', fontWeight: 600 }}>Reply →</Typography>
                          </Box>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Conversation Dialog ───────────────────────────────────────────── */}
      <Dialog
        open={openChat}
        onClose={handleCloseChat}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', height: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        {/* Header */}
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e1e3e5' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1, mr: 1 }}>

              {/* Ticket title + status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.3 }}>
                  #{selectedTicket?.id} — {selectedTicket?.subject}
                </Typography>
                {selectedTicket && (
                  <Chip
                    label={STATUS_LABEL[selectedTicket.status]}
                    size="small"
                    sx={{
                      fontSize: '11px', fontWeight: 600,
                      bgcolor: STATUS_CONFIG[selectedTicket.status].bgcolor,
                      color:   STATUS_CONFIG[selectedTicket.status].color,
                    }}
                  />
                )}
              </Box>

              {/* User info row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                {selectedTicket?.display_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person sx={{ fontSize: 13, color: '#6d7175' }} />
                    <Typography variant="caption" color="text.secondary">
                      {selectedTicket.display_name}
                    </Typography>
                    {selectedTicket.is_guest && (
                      <Chip label="Guest" size="small" sx={{ fontSize: '9px', height: 15, bgcolor: '#fff3cd', color: '#856404', fontWeight: 600 }} />
                    )}
                  </Box>
                )}
                {selectedTicket?.phone !== '-' && selectedTicket?.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ fontSize: 13, color: '#6d7175' }} />
                    <Typography variant="caption" color="text.secondary">{selectedTicket.phone}</Typography>
                  </Box>
                )}
                {selectedTicket?.email !== '-' && selectedTicket?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Email sx={{ fontSize: 13, color: '#6d7175' }} />
                    <Typography variant="caption" color="text.secondary">{selectedTicket.email}</Typography>
                  </Box>
                )}
                {selectedTicket?.invoice_no && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Receipt sx={{ fontSize: 13, color: '#6d7175' }} />
                    <Typography variant="caption" color="text.secondary">{selectedTicket.invoice_no}</Typography>
                  </Box>
                )}
              </Box>

            </Box>
            <IconButton onClick={handleCloseChat} size="small" sx={{ flexShrink: 0 }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Chat body */}
        <DialogContent sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f8f9fa' }}>
          {loadingChat ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={28} sx={{ color: '#008060' }} />
            </Box>
          ) : replies.length === 0 ? (
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Typography variant="body2" color="text.secondary">No messages yet</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {replies.map((reply, idx) => {
                // Prefer the backend's sender_type (accurate even when the
                // same side sends multiple messages in a row). Fall back to
                // the old "index 0 = customer, rest = admin" guess only if
                // an older API response without sender_type is ever hit.
                const isAdmin = reply.sender_type
                  ? reply.sender_type === 'admin'
                  : idx > 0;
                return (
                  <Box
                    key={idx}
                    sx={{ display: 'flex', flexDirection: isAdmin ? 'row-reverse' : 'row', gap: 1, alignItems: 'flex-end' }}
                  >
                    <Avatar sx={{ width: 28, height: 28, bgcolor: isAdmin ? '#008060' : '#e1e3e5', flexShrink: 0 }}>
                      {isAdmin
                        ? <AdminPanelSettings sx={{ fontSize: 16, color: 'white' }} />
                        : <Person sx={{ fontSize: 16, color: '#6d7175' }} />}
                    </Avatar>
                    <Box
                      sx={{
                        maxWidth: '75%',
                        bgcolor: isAdmin ? '#008060' : 'white',
                        color:   isAdmin ? 'white'   : '#202223',
                        borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        px: 2, py: 1.25,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                        border: isAdmin ? 'none' : '1px solid #e1e3e5',
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.3, opacity: 0.85, fontSize: '10px' }}>
                        {reply.sender_name || (isAdmin ? 'Support Team' : (selectedTicket?.display_name ?? 'User'))}
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.5, wordBreak: 'break-word' }}>
                        {reply.message}
                      </Typography>
                      {reply.created_at && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.65, fontSize: '10px', textAlign: isAdmin ? 'left' : 'right' }}>
                          {formatDate(reply.created_at)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
              <div ref={bottomRef} />
            </Box>
          )}
        </DialogContent>

        <Divider />

        {/* Reply area */}
        <DialogActions sx={{ flexDirection: 'column', gap: 1.5, p: 2, alignItems: 'stretch' }}>

          {/* Status selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Set status:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={String(replyStatus)}
                onChange={(e: SelectChangeEvent) => setReplyStatus(Number(e.target.value) as TicketStatusNum)}
                sx={{ borderRadius: '8px', fontSize: '13px' }}
              >
                {([0, 1, 2, 3] as TicketStatusNum[]).map(s => (
                  <MenuItem key={s} value={String(s)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {STATUS_CONFIG[s].icon}
                      <Typography variant="body2">{STATUS_LABEL[s]}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Text input + send */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              multiline
              maxRows={4}
              size="small"
              placeholder="Type your reply… (Enter to send)"
              fullWidth
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
            />
            <Button
              variant="contained"
              onClick={handleSendReply}
              disabled={!replyText.trim() || sendingReply}
              sx={{
                bgcolor: '#008060', minWidth: 0, width: 44, height: 44,
                borderRadius: '12px', p: 0, flexShrink: 0,
                '&:hover': { bgcolor: '#006e52' },
                '&:disabled': { bgcolor: '#c9cccf' },
              }}
            >
              {sendingReply
                ? <CircularProgress size={18} color="inherit" />
                : <Send sx={{ fontSize: 18, color: 'white' }} />}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportManagement;